import React, { useRef, useMemo, useCallback, useState } from 'react';
import {
    Timeline,
    TimelineHeader,
    TimelineBodyContainer,
    TimelineScrollContainer,
    TimelineContainer,
    TimelineTrackContainer,
    TimelineTrack,
    RealtimeTracker,
    LiveButton,
    SpeedoMeter,
} from '../../../../CommonComponents/timeline/Timeline';
import { PlayPauseSvg, PreviousSvg, NextSvg } from '../../../../CommonComponents/timeline/Svg';
import useGenericSimulator from '../../../hooks/useGenericSimulator';
import { useSimulationTimer } from '../../../hooks/useSimulationTimer';
import useUrlParams from '../../../hooks/useUrlParams';
import useSummaryReplay from '../../../hooks/useSummaryReplay';
import './LiveTimeline.css';

/** Full speed range: -50x to 50x (negative = reverse playback) */
const SPEED_OPTIONS = [-50, -20, -10, -5, -2, -1, -0.5, 0.5, 1, 2, 5, 10, 20, 50];

/**
 * LiveTimeline — a compact CommonComponents-based timeline shown at the bottom
 * of the generic simulator dashboard in **both** live and replay modes.
 *
 * - Live mode: shows time track + live cursor that auto-advances + Live button.
 * - Replay mode: shows time track + draggable cursor + play/pause/speed controls.
 */
const LiveTimeline: React.FC = () => {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const { replayState, activeTab, visualizationData } = useGenericSimulator();
    const { dashboard } = useUrlParams();
    const isReplayMode = dashboard === 'summary';
    const isSummaryLoading = isReplayMode && !visualizationData;

    // --- Live mode timer ---
    const {
        currentSimulationTimeSec,
        scenarioStartTimeAbs,
        scenarioEndTime,
        simulationDurationMins,
    } = useSimulationTimer();

    // --- Replay mode controls ---
    const { play, pause, seek, setSpeed } = useSummaryReplay();

    // Timeline component expects epoch times (seconds)
    const timelineStartTime = scenarioStartTimeAbs;
    const timelineEndTime = scenarioStartTimeAbs + scenarioEndTime;
    const timelineDuration = simulationDurationMins;

    // --- Drag state: keeps the tracker thumb stable during drag ---
    const [isDragging, setIsDragging] = useState(false);
    const [dragTimeEpoch, setDragTimeEpoch] = useState<number>(0);
    const wasPlayingRef = useRef(false);
    const isDraggingRef = useRef(false);

    // Current time in epoch seconds
    const currentTimeEpoch = useMemo(() => {
        // While dragging, show the locally-tracked drag position instead of
        // the (async) replay state — prevents thumb snap-back.
        if (isDragging) return dragTimeEpoch;
        if (isReplayMode) {
            // replayState.currentTimeMs is relative ms from 0
            return timelineStartTime + (replayState.currentTimeMs / 1000);
        }
        // live: relative seconds from 0
        return timelineStartTime + currentSimulationTimeSec;
    }, [isDragging, dragTimeEpoch, isReplayMode, timelineStartTime, currentSimulationTimeSec, replayState.currentTimeMs]);

    // Replay: when user drags the tracker, convert epoch back to relative ms and seekTo
    const handleTrackerChange = useCallback((epochSec: number) => {
        if (!isReplayMode) return;

        // On first drag event, pause playback and remember the previous state
        if (!isDraggingRef.current) {
            wasPlayingRef.current = replayState.status === 'playing';
            if (wasPlayingRef.current) pause();
            isDraggingRef.current = true;
            setIsDragging(true);
        }

        // Keep the thumb in sync locally (no waiting for React state roundtrip)
        setDragTimeEpoch(epochSec);

        // Seek to the new position
        const relativeMs = (epochSec - timelineStartTime) * 1000;
        seek(Math.max(0, relativeMs));
    }, [isReplayMode, timelineStartTime, seek, pause, replayState.status]);

    // When the mouse is released after dragging, finalize the seek
    React.useEffect(() => {
        if (!isDragging) return;
        const handleMouseUp = () => {
            isDraggingRef.current = false;
            setIsDragging(false);
            // Resume playback if it was playing before the drag started
            if (wasPlayingRef.current) {
                play();
            }
        };
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [isDragging, play]);

    // Replay: play/pause state
    const isPlaying = replayState.status === 'playing';
    const controllerState: 'play' | 'pause' = isPlaying ? 'play' : 'pause';
    const handlePlayPause = useCallback(() => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    }, [isPlaying, play, pause]);

    // Find current index in SPEED_OPTIONS for the active speed
    const currentSpeedIdx = useMemo(() => {
        const idx = SPEED_OPTIONS.indexOf(replayState.speed);
        // If not found (e.g. speed was set externally), find nearest
        if (idx >= 0) return idx;
        return SPEED_OPTIONS.reduce((best, val, i) =>
            Math.abs(val - replayState.speed) < Math.abs(SPEED_OPTIONS[best] - replayState.speed) ? i : best, 0
        );
    }, [replayState.speed]);

    // Replay: decrease speed (backward) — steps toward -50x
    const handleBackward = useCallback(() => {
        const prevIdx = currentSpeedIdx > 0 ? currentSpeedIdx - 1 : 0;
        setSpeed(SPEED_OPTIONS[prevIdx]);
    }, [currentSpeedIdx, setSpeed]);

    // Replay: increase speed (forward) — steps toward 50x
    const handleForward = useCallback(() => {
        const nextIdx = currentSpeedIdx < SPEED_OPTIONS.length - 1 ? currentSpeedIdx + 1 : SPEED_OPTIONS.length - 1;
        setSpeed(SPEED_OPTIONS[nextIdx]);
    }, [currentSpeedIdx, setSpeed]);

    // Hide when monitor tab is open or no valid time range
    if (activeTab === 'monitor') return null;
    if (!timelineEndTime || timelineEndTime <= timelineStartTime) return null;

    return (
        <Timeline
            scrollAreaRef={scrollAreaRef}
            timelineStartTime={timelineStartTime}
            timelineEndTime={timelineEndTime}
            timelineDuration={timelineDuration}
            selectedFrame={30}
            show={true}
            showSidebar={false}
            hideToggleButton
            className={`live-timeline ${isSummaryLoading ? 'live-timeline--loading' : ''}`}
        >
            <TimelineHeader>
                <div className="live-timeline__header">
                    {isReplayMode && (
                        <div className="live-timeline__controls-center">
                            <button className="live-timeline__ctrl-btn" onClick={handleBackward} title="Decrease speed">
                                <PreviousSvg active={currentSpeedIdx > 0} />
                                <PreviousSvg active={currentSpeedIdx > 0} />
                            </button>
                            <button className="live-timeline__play-pause" onClick={handlePlayPause} title={isPlaying ? 'Pause' : 'Play'}>
                                <PlayPauseSvg state={controllerState} />
                            </button>
                            <button className="live-timeline__ctrl-btn" onClick={handleForward} title="Increase speed">
                                <NextSvg active={currentSpeedIdx < SPEED_OPTIONS.length - 1} />
                                <NextSvg active={currentSpeedIdx < SPEED_OPTIONS.length - 1} />
                            </button>
                        </div>
                    )}
                    <div className="live-timeline__header-right">
                        {isReplayMode && <SpeedoMeter value={replayState.speed} />}
                        {!isReplayMode && <LiveButton onClick={handlePlayPause} />}
                    </div>
                </div>
            </TimelineHeader>
            <TimelineBodyContainer>
                <TimelineScrollContainer>
                    <TimelineContainer className='z-9'>
                        <TimelineTrackContainer>
                            <TimelineTrack />
                            <RealtimeTracker
                                currentTime={currentTimeEpoch}
                                strtTime={timelineStartTime}
                                endTime={timelineEndTime}
                                duration={timelineDuration}
                                trackerStep={1}
                                disabled={!isReplayMode}
                                onChange={handleTrackerChange}
                            />
                        </TimelineTrackContainer>
                    </TimelineContainer>
                </TimelineScrollContainer>
            </TimelineBodyContainer>
        </Timeline>
    );
};

export default LiveTimeline;
